import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, FolderPlus, Film, Plus, Search } from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { PTXT, folderPath } from "@/components/projects/projectsText";
import { Counters, FolderCard, ProjectCard, MoveDialog } from "@/components/projects/ProjectsParts";

export default function Projects() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const t = PTXT[lang] || PTXT.fr;
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [folders, setFolders] = useState([]);
  const [stats, setStats] = useState(null);
  const [cur, setCur] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("date");
  const [moving, setMoving] = useState(null); // projet (ou dossier) à déplacer

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
  const load = useCallback(async () => {
    try {
      const [p, f, s] = await Promise.all([api.get("/projects"), api.get("/folders"), api.get("/me/stats")]);
      setProjects(p.data.projects); setFolders(f.data.folders); setStats(s.data);
    } catch (e) { err(e); setProjects((x) => x || []); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const searching = q.trim().length > 0;
  const visible = useMemo(() => {
    if (!projects) return [];
    const ql = q.trim().toLowerCase();
    const list = searching ? projects.filter((p) => (p.title || "").toLowerCase().includes(ql)) : projects.filter((p) => (p.folder_id || null) === cur);
    return [...list].sort((a, b) => sort === "name" ? (a.title || "").localeCompare(b.title || "", lang) : new Date(b.updated_at) - new Date(a.updated_at));
  }, [projects, q, cur, sort, searching, lang]);
  const subFolders = useMemo(() => folders.filter((f) => (f.parent_id || null) === cur).sort((a, b) => (a.position - b.position) || a.name.localeCompare(b.name)), [folders, cur]);
  const crumbs = folderPath(folders, cur);

  const act = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { err(e); } };
  const openProject = (p) => navigate(`/studio?project=${p.project_id}`);
  const renameProject = (p) => {
    const title = window.prompt(t.promptTitle, p.title);
    if (!title || title === p.title) return;
    act(async () => { const { data: full } = await api.get(`/projects/${p.project_id}`); await api.post("/projects", { project_id: p.project_id, title, state: full.state, base_revision: full.revision }); });
  };
  const deleteProject = (p) => { if (window.confirm(t.confirmDelete(p.title))) act(() => api.delete(`/projects/${p.project_id}`), t.deleted); };
  const duplicateProject = (p) => act(() => api.post(`/projects/${p.project_id}/duplicate`), t.duplicated);
  const moveProject = (pid, fid) => act(() => api.patch(`/projects/${pid}/folder`, { folder_id: fid }), t.moved);
  const moveFolder = (fid, parent) => act(() => api.patch(`/folders/${fid}`, parent ? { parent_id: parent } : { move_to_root: true }), t.moved);
  const newFolder = () => { const name = window.prompt(t.promptFolder); if (name?.trim()) act(() => api.post("/folders", { name: name.trim(), parent_id: cur }), t.created); };
  const renameFolder = (f) => { const name = window.prompt(t.promptRename, f.name); if (name?.trim() && name !== f.name) act(() => api.patch(`/folders/${f.folder_id}`, { name: name.trim() })); };
  const deleteFolder = (f) => { if (window.confirm(t.confirmDeleteFolder(f.name))) act(() => api.delete(`/folders/${f.folder_id}`), t.deleted); };
  const dropOnCrumb = (e, fid) => { e.preventDefault(); const pid = e.dataTransfer.getData("bc/project"); const df = e.dataTransfer.getData("bc/folder"); if (pid) moveProject(pid, fid); else if (df && df !== fid) moveFolder(df, fid); };

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8 py-8 sm:py-12">
        <p className="font-osd text-xs tracking-[0.25em] text-primary mb-3">{t.tag}</p>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="projects-title">{t.title}</h1>
          <div className="flex gap-2">
            <button onClick={newFolder} data-testid="projects-new-folder" className="inline-flex items-center gap-2 border border-border h-11 px-4 font-osd text-[11px] tracking-[0.08em] uppercase hover:border-foreground transition-colors"><FolderPlus size={14} /> <span className="hidden sm:inline">{t.newFolder}</span></button>
            <Link to="/start" data-testid="projects-new-button" className="inline-flex items-center gap-2 rounded-full bg-primary text-white h-11 px-5 font-osd text-[11px] tracking-[0.08em] uppercase hover:opacity-90 transition-opacity"><Plus size={14} /> {t.newProject}</Link>
          </div>
        </div>
        {user?.anonymous && <p className="mb-6 border border-primary/40 bg-primary/5 px-4 py-3 text-xs text-foreground" data-testid="projects-anon-note">{t.anon}</p>}
        <Counters stats={stats} lang={lang} />

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <nav className="flex items-center gap-1 text-sm w-full sm:w-auto sm:flex-1 min-w-0 overflow-x-auto" aria-label="Fil d'Ariane" data-testid="projects-breadcrumb">
            <button onClick={() => setCur(null)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => dropOnCrumb(e, null)} data-testid="crumb-root" className={`shrink-0 font-osd text-[11px] tracking-[0.14em] uppercase ${cur == null ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{t.root}</button>
            {crumbs.map((f) => (
              <span key={f.folder_id} className="flex items-center gap-1 shrink-0">
                <ChevronRight size={13} className="text-muted-foreground" />
                <button onClick={() => setCur(f.folder_id)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => dropOnCrumb(e, f.folder_id)} data-testid={`crumb-${f.folder_id}`} className={`font-osd text-[11px] tracking-[0.14em] uppercase ${cur === f.folder_id ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{f.name}</button>
              </span>
            ))}
          </nav>
          <label className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.search} data-testid="projects-search" className="bg-background border border-border pl-9 pr-3 h-10 text-sm w-44 sm:w-56 focus:border-[#fc1c46] focus:outline-none" />
          </label>
          <div className="flex border border-border" data-testid="projects-sort">
            {["date", "name"].map((s) => (
              <button key={s} onClick={() => setSort(s)} data-testid={`sort-${s}`} className={`h-10 px-3 font-osd text-[11px] tracking-[0.08em] uppercase ${sort === s ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>{s === "date" ? t.sortDate : t.sortName}</button>
            ))}
          </div>
        </div>

        {projects === null ? (
          <p className="font-osd text-sm text-muted-foreground animate-pulse">…</p>
        ) : (
          <>
            {!searching && subFolders.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="folders-grid">
                {subFolders.map((f) => <FolderCard key={f.folder_id} folder={f} lang={lang} onOpen={setCur} onRename={renameFolder} onDelete={deleteFolder} onDropProject={moveProject} onDropFolder={moveFolder} />)}
              </div>
            )}
            {searching && <p className="font-osd text-[11px] tracking-wider uppercase text-muted-foreground mb-4" data-testid="projects-results">{visible.length ? t.results(visible.length) : t.noResults}</p>}
            {visible.length === 0 && !searching ? (
              <div className="bg-card border border-border p-12 text-center" data-testid="projects-empty">
                <Film size={40} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-2">{cur ? t.emptyFolder : t.empty}</p>
                {!cur && <p className="text-sm text-muted-foreground">{t.emptyHint}</p>}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
                {visible.map((p) => <ProjectCard key={p.project_id} p={p} lang={lang} onOpen={openProject} onRename={renameProject} onDuplicate={duplicateProject} onMove={setMoving} onDelete={deleteProject} />)}
              </div>
            )}
          </>
        )}
      </div>
      <MoveDialog open={!!moving} folders={folders} current={moving?.folder_id || null} lang={lang} onClose={() => setMoving(null)} onPick={(fid) => { const p = moving; setMoving(null); moveProject(p.project_id, fid); }} />
    </AppShell>
  );
}
